/**
 * PopoverSelect Hook
 *
 * Handles positioning and option selection for PopoverSelect component.
 * Uses the HTML Popover API for reliable dropdown behavior.
 *
 * Browser Support:
 * - Chrome 114+, Safari 17+, Firefox 125+
 * - Falls back gracefully on unsupported browsers
 */

const MENU_GAP = 4 // Gap between button and dropdown
const VIEWPORT_PADDING = 8 // Minimum distance from viewport edges
const ICON_WIDTH = 16 // Chevron icon width in pixels
const ICON_GAP = 8 // Gap between text and icon in pixels

const PopoverSelectHook = {
  mounted() {
    if (!this.isPopoverSupported()) {
      console.warn("PopoverSelect: Popover API not supported in this browser.")
      return
    }

    this.inputId = this.el.dataset.inputId
    this.buttonId = this.el.dataset.buttonId
    this.button = document.getElementById(this.buttonId)
    this.autoWidth = this.el.dataset.autoWidth !== "false"

    if (this.autoWidth) {
      // Sync widths on mount by measuring dropdown's natural width
      this.syncWidths()
    } else {
      // Let CSS control the trigger width, but keep the popover menu aligned to it.
      this.syncMenuWidthToButton()
      if (this.button) this.button.classList.remove("invisible")
    }

    // Pre-position the dropdown BEFORE it becomes visible (ENA-8690)
    // beforetoggle fires synchronously before the popover enters the top layer,
    // so we can measure and position it while it's still hidden.
    this.el.addEventListener("beforetoggle", (event) => {
      if (event.newState !== "open" || !this.button) return

      // Temporarily make measurable while still invisible
      this.el.style.display = "block"
      this.el.style.visibility = "hidden"

      if (!this.autoWidth) this.syncMenuWidthToButton()
      this.positionDropdown(this.button)

      // Reset — showPopover() will set display via :popover-open
      this.el.style.display = ""
      this.el.style.visibility = ""
    })

    // Handle option clicks
    this.el.addEventListener("click", (event) => {
      const option = event.target.closest("li[data-value]")
      if (!option) return

      const value = option.dataset.value
      this.selectOption(value)
    })
  },

  updated() {
    // After LiveView re-renders, morphdom may replace the button element or restore
    // the server-rendered "invisible" class. Re-fetch the button reference, re-apply
    // the stored width (only when auto-width is enabled), and remove invisible class.
    this.button = document.getElementById(this.buttonId)
    if (this.button) {
      if (this.autoWidth && this._syncedWidth) {
        this.el.style.width = `${this._syncedWidth}px`
        this.button.style.width = `${this._syncedWidth}px`
      } else if (!this.autoWidth) {
        this.syncMenuWidthToButton()
      }
      this.button.classList.remove("invisible")
    }
  },

  isPopoverSupported() {
    return (
      typeof HTMLElement !== "undefined" &&
      typeof HTMLElement.prototype.showPopover === "function"
    )
  },

  syncWidths() {
    if (!this.button) return

    // Temporarily show popover off-screen to measure its natural width
    this.el.style.visibility = "hidden"
    this.el.style.position = "fixed"
    this.el.style.top = "-9999px"
    this.el.style.left = "-9999px"
    this.el.showPopover()

    // Use dropdown width as the base - it's based on longest option
    const menuWidth = this.el.getBoundingClientRect().width

    // Button needs extra space for the chevron icon + gap
    const buttonWidth = menuWidth + ICON_WIDTH + ICON_GAP

    // Apply and store fixed widths
    this._syncedWidth = buttonWidth
    this.el.style.width = `${buttonWidth}px`
    this.button.style.width = `${buttonWidth}px`

    // Hide popover and reset its position
    this.el.hidePopover()
    this.el.style.visibility = ""
    this.el.style.top = ""
    this.el.style.left = ""

    // Now show the button (it was hidden via CSS to prevent layout shift)
    this.button.classList.remove("invisible")
  },

  syncMenuWidthToButton() {
    if (!this.button) return

    const buttonWidth = this.button.getBoundingClientRect().width
    this.el.style.width = `${buttonWidth}px`
  },

  positionDropdown(button) {
    const buttonRect = button.getBoundingClientRect()
    const menuRect = this.el.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Position below the button, aligned to left edge
    let top = buttonRect.bottom + MENU_GAP
    let left = buttonRect.left

    // Keep within viewport bounds
    if (left + menuRect.width > viewportWidth - VIEWPORT_PADDING) {
      left = viewportWidth - menuRect.width - VIEWPORT_PADDING
    }
    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING
    }

    // Flip above if no room below
    if (top + menuRect.height > viewportHeight - VIEWPORT_PADDING) {
      top = buttonRect.top - menuRect.height - MENU_GAP
    }
    if (top < VIEWPORT_PADDING) {
      top = VIEWPORT_PADDING
    }

    this.el.style.position = "fixed"
    this.el.style.top = `${top}px`
    this.el.style.left = `${left}px`
  },

  selectOption(value) {
    const input = document.getElementById(this.inputId)
    if (!input) return

    // Update hidden input value
    input.value = value

    // Dispatch input event for LiveView to detect the change
    input.dispatchEvent(new Event("input", { bubbles: true }))

    // Close the popover
    this.el.hidePopover()
  },
}

export default PopoverSelectHook
